// Single source of truth for per-route <head> metadata.
//
// Two render paths consume these strings, so they can never drift:
//   • worker.js rewrites the static index.html shell *server-side* (via
//     HTMLRewriter) for /stats and /privacy, so non-JS clients — social
//     scrapers (Facebook, iMessage, Slack, LinkedIn) and simpler crawlers —
//     get the right title/description/canonical/OG, and Google never sees a
//     sub-route declaring the homepage as its canonical.
//   • the React route components re-assert the same values client-side via
//     useMeta() during in-app SPA navigation.
//
// The homepage's <head> is hand-authored in index.html (it keeps a distinct,
// punchier og:title/og:description tuned for social cards) and is intentionally
// NOT rewritten by the worker — but its title/description live here too so the
// client restores the exact same values when navigating back to "/".

import { MAIN_HOST, SITE_URL, SWITCHED_TO_GP85 } from "./siteAddress.js";
import { SHOW_NAME, TIME_WORDS, dayShort, gp85Path, nightOf, nights } from "./gp85/season.js";
import { albumJsonLd, gameJsonLd, homeJsonLd } from "./gp85/seo.js";

export { SITE_URL };
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/greg-yankees.jpg`;

// The share picture for the show's pages: Greg in the booth (the /live card).
const BOOTH_CARD = {
  image: `${SITE_URL}/assets/share/live-booth.jpg`,
  imageAlt:
    "Greg Pryor in his booth, in a Royals pullover in front of a ballpark backdrop, with an On Air sign and the words Live from The Booth.",
  imageWidth: 1200,
  imageHeight: 630,
};

// The gp85 pages are a preview at /gp85 until the move to gp85.com, and a
// preview is kept out of search (it would be the same site twice). After
// the move they are the site, and indexed. See SETUP.md §7.
export const GP85_ROBOTS = SWITCHED_TO_GP85 ? "" : "noindex, follow";

// The nights' pages, by their slug (/gp85/games/<slug>).
export const GP85_GAME_META = Object.fromEntries(
  nights().map((n) => [
    n.slug,
    {
      path: n.path,
      title: `${n.title} with Greg Pryor — ${dayShort(n.date)}, ${TIME_WORDS}, free`,
      description: `${n.blurb} ${dayShort(n.date)} at ${TIME_WORDS}: Greg calls the original ${n.year} broadcast live from his living room. Free, nothing to install.`,
      ...BOOTH_CARD,
      jsonLd: gameJsonLd(n.slug),
    },
  ]),
);
export const gp85GameMeta = (slug) => GP85_GAME_META[nightOf(slug)?.slug] || null;

export const ROUTE_META = {
  home: {
    path: "/",
    title: "Greg Pryor — Royals №4 · World Series Champion · Author",
    description:
      "Greg Pryor — MLB infielder for the 1985 World Champion Kansas City Royals. Book, signed memorabilia, speaking engagements, and stories from the dugout.",
  },
  stats: {
    path: "/stats",
    title: "Career Stats — Greg Pryor (Royals №4, 1976–1986)",
    description:
      "Every pitcher Greg faced, every team he beat, every split. Ten MLB seasons of at-bats — searchable, with a natural-language Q&A backed by primary sources.",
  },
  ama: {
    path: "/ama",
    title: "The Reddit AMA — Greg Pryor takes r/baseball's questions (2018)",
    description:
      "In April 2018 Greg Pryor answered everything r/baseball threw at him — Disco Demolition from a padlocked clubhouse, the Pine Tar Game, Bo Jackson, the '85 Royals, pizza and mustaches. The complete AMA, word for word, with photos and verified stats.",
  },
  presentation: {
    path: "/presentation",
    title: "Greg Pryor, Live — the hour-long show in 22 slides",
    description:
      "Greg's speaking deck: Disco Demolition, the Pine Tar Game, three walk-off homers, Minnie Miñoso pinch-hitting for him, Bo Jackson's debut, George Brett's 2,000th hit on a borrowed bat, and the 1985 World Series — every fact verified against MLB's own records.",
  },
  live: {
    path: "/live",
    title: "Greg Pryor, Live from the Booth — postseason commentary",
    description:
      "Former Royals infielder Greg Pryor calls the postseason live: his camera and mic, plus the live score, pitch tracker, win probability, and a scorebook that fills itself in. Ask him anything in The Stands. He answers on the air.",
    // Share-card picture (Max's pick, Oct 25 2026): Greg in the booth's World
    // Series set, the stadium backdrop from Opening Night (an Oct 23 broadcast
    // frame Max cleared), with "Live from The Booth". A new file name, so
    // Facebook and iMessage don't keep showing a cached old picture.
    image: `${SITE_URL}/assets/share/live-booth.jpg`,
    imageAlt:
      "Greg Pryor in his booth, in a Royals pullover in front of a ballpark backdrop, with an On Air sign and the words Live from The Booth.",
    imageWidth: 1200,
    imageHeight: 630,
  },
  buy: {
    path: "/buy",
    title: "Get a Signed Copy — The Day The Yankees Made Me Shave by Greg Pryor",
    description:
      "Order Greg Pryor's memoir direct from Greg — hand-signed, personally inscribed if you'd like, and shipped straight from him. This link opens checkout right away.",
  },
  privacy: {
    path: "/privacy",
    title: "Privacy Policy — Greg Pryor",
    description: `How ${MAIN_HOST} handles the data you share when ordering a signed book or sending Greg a message — plain-English, with named service providers.`,
  },
  shave: {
    path: "/the-day-the-yankees-made-me-shave",
    title: "The Day the Yankees Made Me Shave — Greg Pryor & the Mustache Revolt",
    description:
      "In 1977 the Yankees ordered Greg Pryor to shave his mustache — so he started the Mustache Revolt. The firsthand story behind the no-beard rule the Yankees finally dropped in 2025.",
  },
  foreword: {
    path: "/foreword-by-tony-la-russa",
    title: "Foreword by Tony La Russa — The Day The Yankees Made Me Shave (Greg Pryor)",
    description:
      "Hall of Fame manager Tony La Russa's foreword to Greg Pryor's memoir — managing Greg with the White Sox, trading him to the Royals, and the walk-off home runs that became Greg's revenge.",
  },
  notFound: {
    title: "Page not found — Greg Pryor",
    description: `That URL doesn't lead anywhere on ${MAIN_HOST}.`,
  },
  // The show's home: the site as it will be on gp85.com.
  gp85: {
    path: gp85Path("/"),
    title: `${SHOW_NAME} — Greg Pryor calls his old games live, Thursday nights`,
    description:
      "Every Thursday night this winter, former Royals infielder Greg Pryor puts on one of his old games and calls it pitch by pitch from his living room: the 1985 World Series, the Pine Tar Game, Disco Demolition Night. Free, nothing to install.",
    ...BOOTH_CARD,
    jsonLd: homeJsonLd(),
  },
  gp85Album: {
    path: gp85Path("/album"),
    title: "The album — 31 photos and a film from Greg Pryor's life in baseball",
    description:
      "Greg Pryor's photo album: Marietta, the minors, Comiskey Park, Kansas City, the 1985 World Series and the booth. Tap any photo to see it big.",
    jsonLd: albumJsonLd(),
  },
};

// The show's tags for a path under its root ("/" , "/album", "/games/<slug>"),
// or null when there's no such page. The Worker asks this for every request
// under /gp85 (and for the root once the site has moved to gp85.com).
export function gp85MetaFor(rel) {
  if (rel === "/" || rel === "") return ROUTE_META.gp85;
  if (rel === "/album" || rel === "/album/") return ROUTE_META.gp85Album;
  const m = rel.match(/^\/games\/([a-z0-9-]+)\/?$/i);
  return m ? GP85_GAME_META[m[1].toLowerCase()] || null : null;
}
